import styled from '@emotion/styled';
import { Button, colors, type SelectOption } from '@innovationdepartment/proxima-ui';
import { useEffect, useState } from 'react';
import type { BrandTag } from 'types/brandTag';
import { formatDateTimeFromIso } from 'utils';
import BrandsUsingTag from './BrandsUsingTag';
import {
  BackLink,
  CheckboxLabel,
  CheckboxRow,
  ContentSection,
  FormField,
  FormRow,
  Header,
  HeaderLeft,
  Label,
  LoadingText,
  PageWrapper,
  SectionTitle,
  StyledCheckbox,
  StyledInput,
  StyledSelect,
  TextArea,
  Title
} from './BrandTags.styles';

// Local unique components
const ReadOnlyValue = styled.div`
  padding: 8px 12px;
  background-color: ${colors.neutral5};
  border-radius: 4px;
  font-size: 14px;
  color: ${colors.neutral60};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;
`;

const FlexSpacer = styled.div`
  flex: 1;
`;

export interface TagDetailViewProps {
  tag: BrandTag | null;
  editedTag: BrandTag | null;
  errors: Set<string>;
  onFieldChange: (field: keyof BrandTag, value: string | boolean) => void;
  onBack: () => void;
  onSave: () => void;
  onReset: () => void;
  onDelete: () => void;
  isDirty: boolean;
  hasBrandsUsingTag: boolean;
  categoryOptions: Array<SelectOption>;
  subCategoryOptions: Array<SelectOption>;
  genderOptions: Array<SelectOption>;
  onCategoryChange: (value: string) => void;
  onSubCategoryChange: (value: string) => void;
  dataLoaded: boolean;
  onBrandCountChange: (count: number) => void;
}

const BrandTagDetailView = ({
  tag,
  editedTag,
  errors,
  onFieldChange,
  onBack,
  onSave,
  onReset,
  onDelete,
  isDirty,
  hasBrandsUsingTag,
  categoryOptions,
  subCategoryOptions,
  genderOptions,
  onCategoryChange,
  onSubCategoryChange,
  dataLoaded,
  onBrandCountChange
}: TagDetailViewProps) => {
  // Use keys to force re-render of Select components when values change
  const [categoryKey, setCategoryKey] = useState(0);
  const [subCategoryKey, setSubCategoryKey] = useState(0);

  // Force re-render when category value changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (editedTag) {
      setCategoryKey(prev => prev + 1);
    }
  }, [editedTag?.category]);

  // Force re-render when subcategory value changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (editedTag) {
      setSubCategoryKey(prev => prev + 1);
    }
  }, [editedTag?.subCategory]);

  if (!tag || !editedTag || !dataLoaded) {
    return (
      <PageWrapper>
        <LoadingText>Loading...</LoadingText>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Header>
        <HeaderLeft>
          <BackLink onClick={onBack}>[← Back to Tags]</BackLink>
          <Title>TAG DETAILS</Title>
        </HeaderLeft>
      </Header>

      <ContentSection>
        <SectionTitle>TAG PROPERTIES</SectionTitle>

        <FormRow>
          <FormField flex={2}>
            <Label>Tag ID:</Label>
            <ReadOnlyValue>{tag.id}</ReadOnlyValue>
          </FormField>
          <FormField flex={2}>
            <Label>Created By:</Label>
            <ReadOnlyValue>{tag.createdBy.replace('google-apps|', '')}</ReadOnlyValue>
          </FormField>
          <FormField flex={2}>
            <Label>Created At:</Label>
            <ReadOnlyValue>{formatDateTimeFromIso(tag.createdAt)}</ReadOnlyValue>
          </FormField>
          <FormField flex={2}>
            <Label>Updated At:</Label>
            <ReadOnlyValue>{formatDateTimeFromIso(tag.updatedAt)}</ReadOnlyValue>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField flex={2}>
            <Label hasError={errors.has('originalName')}>Original Name:</Label>
            <StyledInput
              type="text"
              value={editedTag.originalName}
              onChange={e => onFieldChange('originalName', e.target.value)}
              placeholder="Enter original name"
              hasError={errors.has('originalName')}
              data-testid="original-name-input"
            />
          </FormField>
          <FormField flex={2}>
            <Label hasError={errors.has('displayName')}>Display Name:</Label>
            <StyledInput
              type="text"
              value={editedTag.displayName}
              onChange={e => onFieldChange('displayName', e.target.value)}
              placeholder="Enter display name"
              hasError={errors.has('displayName')}
              data-testid="display-name-input"
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('description')}>Description:</Label>
            <TextArea
              value={editedTag.description}
              onChange={e => onFieldChange('description', e.target.value)}
              placeholder="Enter description"
              hasError={errors.has('description')}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('category')}>Category:</Label>
            <StyledSelect
              key={categoryKey}
              value={
                categoryOptions.some(opt => opt.value === editedTag.category)
                  ? editedTag.category
                  : ''
              }
              onChange={e => onCategoryChange(e.target.value)}
              hasError={errors.has('category')}
            >
              {categoryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StyledSelect>
          </FormField>
          <FormField>
            <Label hasError={errors.has('subCategory')}>Sub-Category:</Label>
            <StyledSelect
              key={subCategoryKey}
              value={editedTag.subCategory}
              onChange={e => onSubCategoryChange(e.target.value)}
              hasError={errors.has('subCategory')}
            >
              <option value="">Select sub-category</option>
              {subCategoryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StyledSelect>
          </FormField>
          <FormField flex={2}>
            <Label hasError={errors.has('genderRelevance')}>Gender Relevance:</Label>
            <StyledSelect
              value={editedTag.genderRelevance}
              onChange={e => onFieldChange('genderRelevance', e.target.value)}
              hasError={errors.has('genderRelevance')}
            >
              {genderOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StyledSelect>
          </FormField>
          <FormField flex={2} />
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('typeHelperText')}>Type Helper Text:</Label>
            <TextArea
              value={editedTag.typeHelperText}
              onChange={e => onFieldChange('typeHelperText', e.target.value)}
              placeholder="Enter Type Helper Text"
              hasError={errors.has('typeHelperText')}
            />
          </FormField>
          <FormField>
            <Label hasError={errors.has('typeDisplayName')}>Type Display:</Label>
            <StyledInput
              type="text"
              value={editedTag.typeDisplayName}
              onChange={e => onFieldChange('typeDisplayName', e.target.value)}
              placeholder="Enter Type Display"
              hasError={errors.has('typeDisplayName')}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('brandAssignmentGuidance')}>
              Brand Assignment Guidance:
            </Label>
            <StyledInput
              type="text"
              value={editedTag.brandAssignmentGuidance}
              onChange={e => onFieldChange('brandAssignmentGuidance', e.target.value)}
              placeholder="Enter Brand Assignment Guidance"
              hasError={errors.has('brandAssignmentGuidance')}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('personaRelevanceGuidance')}>
              Persona Relevance Guidance:
            </Label>
            <StyledInput
              type="text"
              value={editedTag.personaRelevanceGuidance}
              onChange={e => onFieldChange('personaRelevanceGuidance', e.target.value)}
              placeholder="Enter Persona Relevance Guidance"
              hasError={errors.has('personaRelevanceGuidance')}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <CheckboxRow>
            <CheckboxLabel>Persona Eligible:</CheckboxLabel>
            <StyledCheckbox
              type="checkbox"
              checked={editedTag.personaEligible}
              onChange={e => onFieldChange('personaEligible', e.target.checked)}
            />
          </CheckboxRow>
          <CheckboxRow>
            <CheckboxLabel>Sensitive:</CheckboxLabel>
            <StyledCheckbox
              type="checkbox"
              checked={editedTag.sensitive}
              onChange={e => onFieldChange('sensitive', e.target.checked)}
            />
          </CheckboxRow>
          <CheckboxRow>
            <CheckboxLabel>Deprecated:</CheckboxLabel>
            <StyledCheckbox
              type="checkbox"
              checked={editedTag.deprecated}
              onChange={e => onFieldChange('deprecated', e.target.checked)}
            />
          </CheckboxRow>
          <FlexSpacer />
          <ButtonRow>
            <Button
              label="Delete"
              size="small"
              color="error"
              onClick={onDelete}
              disabled={hasBrandsUsingTag}
            />
            <Button
              label="Reset"
              size="small"
              color="warning"
              onClick={onReset}
              disabled={!isDirty}
            />
            <Button
              label="Save"
              size="small"
              color="success"
              onClick={onSave}
              disabled={!isDirty}
            />
          </ButtonRow>
        </FormRow>
      </ContentSection>
      <BrandsUsingTag tag={tag} onBrandCountChange={onBrandCountChange} />
    </PageWrapper>
  );
};

export default BrandTagDetailView;
